import { useEffect, useMemo, useRef, useState } from "react";
import categoryService from "@/services/billing/expenses/categoryService";
import { useAbility } from "@/providers/";
import { toast } from "sonner";
import { KeenIcon, DefaultTooltip } from "@/components";
import { Can } from "@casl/react";
import { CategoryModal } from "@/pages/billing";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import DataTable from "datatables.net-react";
import DT from "datatables.net-dt";
import "datatables.net-select-dt";
import "datatables.net-responsive-dt";
import { createRoot } from "react-dom/client";
import { useAuthContext } from "@/auth";
import { FiltersToFormData, getAccessToken } from "@/utils";
import { createDataTableOptions, createDataTableConfig } from "@/utils";
import { ConfirmationModal } from "@/components/modal";
import { useConfirmationModal } from "@/hooks";
import {
  SelectInput,
  DateInput,
  TextInput,
  STATUS_OPTIONS,
  STATUS_ACTIVE_NUMBER_OPTIONS,
  FilterContainer,
  ColumnToggleDropdown,
} from "@/components/TableFilter";
import { cn } from "@/lib/utils";
import { goTo403 } from "@/utils/Navigation";
import { Container } from '@/components/container';

DataTable.use(DT);
const Category = () => {
  const { logout } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [selectedData, setSelectedData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [contactTypeData, setContactTypeData] = useState([]);
  const [taxesData, setTaxesData] = useState([]);
  const [itemId, setItemId] = useState(null);
  const { ability } = useAbility();
  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState({
    active: null,
    from_date: null,
    to_date: null,
    date_range: null,
  });
  const API_URL = import.meta.env.VITE_APP_BASE_URL;
  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef(filterData);
  const formikRef = useRef();
  const canDeleteCategory = ability.can("delete", "expense");
  const canEditCategory = ability.can("handle", "expense");
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();

  useEffect(() => {
    filterRef.current = filterData;
  }, [filterData]);

  const columns = [
    // {
    //   title: "S#",
    //   name: "serial_number",
    //   data: null,
    //   selector: (row, index) => index + 1,
    //   width: "5%",
    //   className: "table_serial_num",
    //   center: true,
    //orderable: true,se,
    //   render: function (data, type, row, meta) {
    //     return meta.row + 1;
    //   },
    // },
    { data: "category_name", title: "Name", name: "category_name" },
    { data: "category_code", title: "Code", name: "category_code" },
    {
      data: "active",
      title: "Status",
      name: "active",
      orderable: true,
      render: function (value) {
        const badgeClass = value === 1 ? "badge-success" : "badge-danger";
        const label = value === 1 ? "Active" : "Inactive";
        return `<span class="badge badge-pill badge-outline ${badgeClass} gap-1 items-center">
            <span class="badge badge-dot size-1.5 ${badgeClass}"></span>
            ${label}
          </span>`;
      },
    },
    { data: "created_at", title: "Created At", name: "created_at" },

    // --- Extended columns below ---
    {
      data: "description",
      title: "Description",
      name: "description",
      render: (data) => (data ? data : "-"),
    },
    {
      data: "deleted_at",
      title: "Deleted At",
      name: "deleted_at",
      render: (data) => (data ? data : "-"),
    },
    {
      data: "updated_at",
      title: "Updated At",
      name: "updated_at",
      render: (data) => (data ? data : "-"),
    },
    {
      title: "Actions",
      data: null,
      orderable: false,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="action-cell-${row.id}"></div>`;
      },
      name: "actions",
    },
  ];

  const defaultVisibleColumns = [
    // 'serial_number',
    'category_name',
    'category_code',
    'active',
    'created_at',
    'actions',
  ];



  const handleSubmit = async (formData) => {
    const isEdit = mode === "edit";
    const action = isEdit
      ? categoryService.updateCategory
      : categoryService.createCategory;
    const args = isEdit ? [formData, itemId] : [formData];
    try {
      isEdit ? setLoading(true) : setIsLoading(true);
      const { data, status } = await action(...args);
      if (status) {
        handleModalClose();
        formikRef.current?.resetForm();
        refresh();
        toast.success(`Category ${isEdit ? "Updated" : "Created"}`, {
          description: `${data.category_name} Category ${isEdit ? "updated" : "created"} successfully.`,
        });
      }
    } catch (error) {
      console.error("Error", error);
      if (error.message == "422 Unprocessable Entity. Validation failed.") {
        const validationErrors = error.errors;
        const formattedErrors = Object.fromEntries(
          Object.entries(validationErrors).map(([key, value]) => [
            key,
            value[0],
          ])
        );

        formikRef.current?.setErrors(formattedErrors);
        toast.error("Validation Error.", {
          description: "Please check your input. Some fields may be invalid.",
        });
        return;
      }
      if (error.message.includes('linked')) {
        toast.error("Can not Update Category.", {
          description: error.message,
          action: {
            label: "Ok",
          },
        });
        return;
      }
      toast.error("Failed to create Category.", {
        description: error.message,
        action: {
          label: "Ok",
        },
      });
    } finally {
      isEdit ? setLoading(false) : setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    closeModal();
    try {
      const { status, data } = await categoryService.deleteCategory(actionId);
      if (status) {
        refresh();
        toast.success("Deleted Category.", {
          description: `${data.category_name} Category deleted successfully.`,
          action: {
            label: "Ok",
          },
        });
      }
    } catch (error) {
      console.error("Error", error);
      toast.error("Failed to delete Category.", {
        description: error.message,
        action: {
          label: "Ok",
        },
      });
    }
  };

  const handleEdit = async (id) => {
    setItemId(id);
    setOpen(true);
    setIsLoading(true);
    setMode("edit");
    try {
      const { data, status } = await categoryService.getSingleCategory(id);
      if (status) {
        setSelectedData(data);
      }
    } catch (error) {
      console.error("Error", error);
      toast.error("Failed to fetch Category.", {
        description:
          "An error occurred while fetching Category. Please try again later.",
        action: {
          label: "Ok",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setItemId(null);
    setSelectedData(null);
    setMode("create");
    setOpen(false);
  };

  const renderCustomActions = (row) => {
    const { id } = row;

    return (
      <DropdownMenu>
        {(canDeleteCategory || canEditCategory) && (
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-gray-600">
              <KeenIcon icon="dots-vertical" className="text-[18px]" />
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="end" className="w-40">
          <Can I="handle" a="expense" ability={ability}>
            <DropdownMenuItem onClick={() => handleEdit(id)}>
              <KeenIcon
                icon="notepad-edit"
                className="mr-0.5 h-4 w-4 cursor-pointer"
              />{" "}
              Edit
            </DropdownMenuItem>
          </Can>
          <Can I="delete" a="expense" ability={ability}>
            <DropdownMenuItem
              onClick={() => openModal(id)}
              className="text-red-600 cusror-pointer"
            >
              <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </Can>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const ajaxConfig = createDataTableConfig('/setup/expense/categories/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[3, 'desc']],
    initComplete: function () {
      const api = this.api();
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          col.visible(false);
        }
      });
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: "current" }).data();

      rows.each((row) => {
        const container = document.getElementById(`action-cell-${row.id}`);
        if (container) {
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(renderCustomActions(row));
        }
      });
    },
  });

  const refresh = () => {
    const table = tableRef.current.dt();
    table.ajax.reload();
  };

  const resetFilters = () => {
    const cleared = {
      active: null,
      from_date: null,
      to_date: null,
      date_range: null,
    };

    setFilterData(cleared);
    filterRef.current = cleared;
    refresh();
  };

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      //console.log('updated',updated)
      return updated;
    });
  };

  const handleToggleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  return (
    <>
      <Container>
        {/* Filters */}
        <FilterContainer
          show={showFilter}
          onApply={() => refresh()}
          onReset={resetFilters}
        >
          <SelectInput
            label="Status"
            value={filterData.active}
            onChange={(val) => handleFilterChange("active", val)}
            staticOptions={STATUS_ACTIVE_NUMBER_OPTIONS}
            customClass="sm:w-[32%] w-full"
          />

          <DateInput
            customClass="sm:w-[32%] w-full"
            label="Created At - (From, To)"
            mode="range"
            value={filterData.date_range}
            onChange={(range) => {
              setFilterData((prev) => ({
                ...prev,
                from_date: range?.from ?? null,
                to_date: range?.to ?? null,
                date_range: range,
              }));
            }}
          />
        </FilterContainer>

        <div className="card min-w-full mb-4">
          <div className="card-header">
            <div className='flex items-center gap-1'>
              <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
              {/* <h3 className="card-title">Expense Categories</h3> */}
            </div>

            <div className="flex items-center flex-wrap gap-2">
              <button
                className="text-xs btn btn-sm btn-primary btn-outline"
                onClick={handleToggleFilter}
              >
                <KeenIcon icon="filter" className="w-fit btn-icon" />
                {showFilter ? "Hide Advanced Filters" : "Advanced Filters"}
              </button>
              <Can I="handle" a="expense" ability={ability}>
                <button
                  className="text-xs btn btn-sm btn-light"
                  onClick={() => {
                    setOpen(true);
                  }}
                >
                  <KeenIcon icon="plus" className="w-fit btn-icon" />
                  Add New
                </button>
              </Can>
            </div>
          </div>

          <DataTable
            id="category-table"
            ajax={ajaxConfig}
            columns={columns}
            options={options}
            ref={tableRef}
          />

          <CategoryModal
            open={open}
            onCloseModal={handleModalClose}
            handleMode={mode}
            data={selectedData}
            onSubmit={handleSubmit}
            createFieldLoading={isLoading}
            updateFieldLoading={loading}
            taxesData={taxesData}
            ref={formikRef}
          />

          <ConfirmationModal
            show={isOpen}
            onClose={closeModal}
            onConfirm={handleDelete}
            message="Are you sure you want to delete this expense category? This action is permanent and cannot be undone."
            title="Are You Sure?"
          />
        </div>
      </Container>
    </>
  );
};

export { Category };
